// PhotoEditor.tsx
import React, { useRef, useState, useEffect } from "react";
import { applyOps, type EditorOps } from "./editorCore";
import { useLevels } from "./useLevels";
import { useExposure } from "./useExposure";
import { useCrop } from "./useCrop";
import { useClone } from "./useClone";
import { useSaturacion } from "./useSaturacion";

interface PhotoEditorProps {
  src: string;
  ops: EditorOps | undefined;
  onSave: (ops: EditorOps) => unknown;
}

const PhotoEditor: React.FC<PhotoEditorProps> = ({
  src,
  onSave,
  ops: prev,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [tool, setTool] = useState<
    "niveles" | "exposicion" | "saturacion" | "recorte" | "clonar"
  >("niveles");

  // Estado global de ops
  const [ops, setOps] = useState<EditorOps>(
    prev ?? { originalWidth: 0, originalHeight: 0, clonar: [] }
  );

  // Hooks de herramientas
  const levelsTool = useLevels(ops, setOps);
  const exposureTool = useExposure(ops, setOps);
  const saturacionTool = useSaturacion(ops, setOps);
  const cropTool = useCrop(ops, setOps);
  const cloneTool = useClone(ops, setOps);

  const toolsMap = {
    niveles: levelsTool,
    exposicion: exposureTool,
    saturacion: saturacionTool,
    recorte: cropTool,
    clonar: cloneTool,
  };

  const activeTool = toolsMap[tool];

  // Cargar imagen
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => {
      imgRef.current = img;
      setOps((prevOps) => ({
        ...prevOps,
        originalWidth: img.width,
        originalHeight: img.height,
      }));
      renderCanvas(img.width, img.height);
    };
  }, [src]);

  const renderCanvas = (width?: number, height?: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !imgRef.current) return;

    const w = width ?? ops.originalWidth;
    const h = height ?? ops.originalHeight;

    canvas.width = w;
    canvas.height = h;

    // Imagen original
    ctx.drawImage(imgRef.current, 0, 0, w, h);

    // Aplicar historial
    applyOps(ctx, ops);

    // Overlay de la herramienta activa
    activeTool.renderOverlay?.(ctx);
  };

  useEffect(() => {
    renderCanvas();
  }, [ops, tool]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    activeTool.onMouseDown?.(x, y);
    renderCanvas();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    activeTool.onMouseMove?.(x, y);
    renderCanvas();
  };

  const handleMouseUp = () => {
    activeTool.onMouseUp?.();
    renderCanvas();
  };

  const getButtonClass = (t: string) =>
    tool === t ? "btn btn-primary" : "btn btn-default";

  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
      <div style={{ display: "flex", gap: 20 }}>
        <canvas
          ref={canvasRef}
          style={{
            border: "1px solid #ccc",
            cursor: tool === "clonar" ? "crosshair" : "default",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <button
              className={getButtonClass("niveles")}
              onClick={() => setTool("niveles")}
            >
              Niveles
            </button>
            <button
              className={getButtonClass("exposicion")}
              onClick={() => setTool("exposicion")}
            >
              Exposición
            </button>
            <button
              className={getButtonClass("saturacion")}
              onClick={() => setTool("saturacion")}
            >
              Saturación
            </button>
            <button
              className={getButtonClass("recorte")}
              onClick={() => setTool("recorte")}
            >
              Recorte
            </button>
            <button
              className={getButtonClass("clonar")}
              onClick={() => setTool("clonar")}
            >
              Clonar
            </button>
          </div>

          {/* Panel de control de la herramienta activa */}
          {activeTool.control}

          <div>
            <button className="btn btn-primary" onClick={() => onSave(ops)}>
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoEditor;
